import React from 'react'
import { memo } from 'react'
import { Modal, Box, IconButton, Stack, Grid, Typography, Chip, Divider, Avatar, Button } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Close as CloseIcon, LocationOn as LocationOnIcon, AttachMoney as AttachMoneyIcon, School as SchoolIcon, Email as EmailIcon, Phone as PhoneIcon, OpenInNew as OpenInNewIcon, CalendarToday as CalendarIcon } from '@mui/icons-material'
import { formatSalary } from './helpers'
import { PRACTICE_TYPE_ENUM } from 'data/enums'

const JobModal = memo(({ job, onClose }) => {
  const theme = useTheme()
  
  // Helper function to convert practice type to string
  const getPracticeTypeString = (value) => {
    // Map numeric values to string keys
    const practiceTypeMap = {
      1: 'FullTime',
      2: 'Locum',
      3: 'Telemedicine',
      4: 'PartTime',
      5: 'PRN',
      6: 'Travel'
    }
    
    // Get the string key from the numeric value
    const stringKey = practiceTypeMap[value]
    
    // Return the display value from the enum
    return PRACTICE_TYPE_ENUM[stringKey] || null
  }

  if (!job) return null

  return (
    <Modal
      open={!!job}
      onClose={onClose}
      aria-labelledby="job-details-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        overflow: 'auto'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 900,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          overflow: 'auto'
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500'
          }}
        >
          <CloseIcon />
        </IconButton>

        <Stack spacing={3}>
          {/* Header */}
          <Box>
            <Typography variant='h4' sx={{ color: theme.palette.primary.main, mb: 1 }}>
              {job.title}
            </Typography>
            {job.employer?.name && (
              <Typography variant='h6' color='text.secondary'>
                {job.employer.name}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
              {job.createdAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarIcon fontSize='small' color='action' />
                  <Typography variant='body2' color='text.secondary'>
                    Posted {new Date(job.createdAt).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
              {job.link && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={() => window.open(job.link, '_blank')}
                >
                  View Original Posting
                </Button>
              )}
            </Box>
          </Box>

          {/* Main Info Grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                {(job.city || job.state) && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Location
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon color='action' />
                      <Typography variant='body1'>
                        {job.city}{job.city && job.state && ', '}{job.state}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {(job.salary || job.salaryMin || job.salaryMax) && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Salary
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AttachMoneyIcon color='action' />
                      <Typography variant='body1'>
                        {formatSalary(job.salary || job.salaryMin || job.salaryMax)}
                      </Typography>
                    </Box>
                    {job.salaryMin && job.salaryMax && (
                      <Typography variant='body2' color='text.secondary' sx={{ ml: 3 }}>
                        Range: {formatSalary(job.salaryMin)} - {formatSalary(job.salaryMax)}
                      </Typography>
                    )}
                  </Box>
                )}

                {job.practiceType && getPracticeTypeString(job.practiceType) && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Practice Type
                    </Typography>
                    <Typography variant='body1'>
                      {getPracticeTypeString(job.practiceType)}
                    </Typography>
                  </Box>
                )}

                {job.candidateType?.name && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Position Type
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SchoolIcon color='action' />
                      <Typography variant='body1'>
                        {job.candidateType.name}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {job.areaType && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Area Type
                    </Typography>
                    <Typography variant='body1'>
                      {job.areaType}
                    </Typography>
                  </Box>
                )}

                {job.hours && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Hours
                    </Typography>
                    <Typography variant='body1'>
                      {job.hours}
                    </Typography>
                  </Box>
                )}

                {job.shiftType && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Shift Type
                    </Typography>
                    <Typography variant='body1'>
                      {job.shiftType}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                {job.specialties?.length > 0 && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Specialties
                    </Typography>
                    <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                      {job.specialties.map((specialty) => (
                        <Chip
                          key={specialty?.id}
                          label={specialty?.name}
                          sx={{ backgroundColor: theme.palette.primary.light }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {job.subspecialties?.length > 0 && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Subspecialties
                    </Typography>
                    <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                      {job.subspecialties.map((subspecialty) => (
                        <Chip
                          key={subspecialty?.id}
                          label={subspecialty?.name}
                          variant='outlined'
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {(job.visaSponsorship || job.loanForgiveness || job.pslfCompatible) && (
                  <Box>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Benefits & Eligibility
                    </Typography>
                    <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
                      {job.visaSponsorship && (
                        <Chip
                          label='Visa Sponsorship Available'
                          color='success'
                          variant='filled'
                          size='small'
                        />
                      )}
                      {job.loanForgiveness && (
                        <Chip
                          label='Loan Forgiveness Available'
                          color='success'
                          variant='filled'
                          size='small'
                        />
                      )}
                      {job.pslfCompatible && (
                        <Chip
                          label='PSLF Eligible'
                          color='success'
                          variant='filled'
                          size='small'
                        />
                      )}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Grid>
          </Grid>

          {/* Contact Information */}
          {(job.employerUser || job.user) && (
            <>
              <Divider />
              <Box>
                <Typography variant='h6' sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  Contact Information
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: theme.palette.grey[50]
                }}>
                  <Avatar 
                    sx={{ 
                      width: 48, 
                      height: 48,
                      bgcolor: theme.palette.primary.main,
                      fontSize: '1rem'
                    }}
                  >
                    {(job.employerUser?.user?.firstName?.[0] || job.user?.firstName?.[0] || '')}
                    {(job.employerUser?.user?.lastName?.[0] || job.user?.lastName?.[0] || '')}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 'medium' }}>
                      {job.employerUser?.user?.firstName || job.user?.firstName} {job.employerUser?.user?.lastName || job.user?.lastName}
                    </Typography>
                    {job.employerUser?.jobTitle && (
                      <Typography variant='body2' color='text.secondary'>
                        {job.employerUser.jobTitle}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                      {(job.employerUser?.user?.showEmail || job.user?.showEmail) && (job.employerUser?.user?.email || job.user?.email) && (
                        <Button
                          size='small'
                          startIcon={<EmailIcon />}
                          onClick={() => window.open(`mailto:${job.employerUser?.user?.email || job.user?.email}`)}
                        >
                          Email
                        </Button>
                      )}
                      {(job.employerUser?.user?.showPhone || job.user?.showPhone) && (job.employerUser?.user?.phone || job.user?.phone) && (
                        <Button
                          size='small'
                          startIcon={<PhoneIcon />}
                          onClick={() => window.open(`tel:${job.employerUser?.user?.phone || job.user?.phone}`)}
                        >
                          Call
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {/* Job Description */}
          {(job.about || job.aboutRaw) && (
            <>
              <Divider />
              <Box>
                <Typography variant='h6' sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  Job Description
                </Typography>
                <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
                  {job.about || job.aboutRaw}
                </Typography>
              </Box>
            </>
          )}

          {/* Employer Description */}
          {job.employer?.about && (
            <>
              <Divider />
              <Box>
                <Typography variant='h6' sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  About {job.employer?.name || 'the Employer'}
                </Typography>
                <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
                  {job.employer.about}
                </Typography>
              </Box>
            </>
          )}

          {/* Additional Information */}
          {job.academicType && (
            <>
              <Divider />
              <Box>
                <Typography variant='h6' sx={{ color: theme.palette.primary.main, mb: 2 }}>
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant='subtitle2' color='text.secondary'>
                      Academic Type
                    </Typography>
                    <Typography variant='body2'>
                      {job.academicType}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Modal>
  )
})

JobModal.displayName = 'JobModal'

export default JobModal